import { ZodError } from 'zod'
import { oloApi } from '@/lib/apiClient'
import { oloUrls } from '@/lib/urlBuilder'
import { ApiError, CartApiError, normalizeZodError } from '@/lib/apiErrors'
import { CalculationResponseSchema } from '@/shared/schemas/calculationResponse'
import type { CalculationDomainError, CalculationResponse } from '@/shared/types/cart'
import type { TipSettings } from '@/shared/api/location'

// ---------------------------------------------------------------------------
// Calculate API — request shapes
// These describe the wire format sent to POST /api/v2/public/:ref/cart/calculate.
// They are separate from the domain types in src/shared/types/cart.ts — those
// describe Redux state. These describe the serialised request body.
// ---------------------------------------------------------------------------

export interface CalculateCartItemModifier {
	modifierSetRef: string
	modifierRef: string
	quantity: number
	modifiers: CalculateCartItemModifier[]
}

export interface CalculateCartItem {
	ref: string // Ember primaryKey — serialized as 'ref' per app/serializers/application.js primaryKey: 'ref'
	locationRef: string // from app/models/cart-item.js
	itemRef: string
	quantity: number
	specialRequest: string | null
	modifiers: CalculateCartItemModifier[]
}

export interface CalculateCartRequest {
	items: CalculateCartItem[]
	orderTypeRef: string
	menuScheduleRef: string | null
	isAtCheckout: boolean
	discounts?: { discountRef: string; source: string }[]
	couponCode?: string
	// Ember: ticket.js:326 — data.tips = this.get('tipSettings') || undefined
	// The server requires the full location tip configuration to compute tipAmount1/2/3.
	// This is the static tipSettings object from the location API — not the user's selection.
	tips?: TipSettings
}

function isCalculationDomainError(value: unknown): value is CalculationDomainError {
	const v = value as { code?: unknown; metadata?: unknown }
	return (
		typeof value === 'object' &&
		value !== null &&
		typeof v.code === 'string' &&
		(v.metadata === undefined || v.metadata === null || typeof v.metadata === 'object')
	)
}

export function calculateCart(
	ref: string,
	body: CalculateCartRequest,
): Promise<CalculationResponse> {
	return oloApi
		.post<CalculateCartRequest, unknown>(oloUrls.calculateCart(ref), body)
		.then((raw) => CalculationResponseSchema.parse(raw))
		.catch((err: unknown) => {
			// ZodError branch must come first — a schema parse failure inside .then() flows
			// into this catch as ZodError, not ApiError. Wrap it as ApiError with SCHEMA_ERROR
			// so all callers receive a consistent ApiError type.
			if (err instanceof ZodError) throw normalizeZodError(err)

			// apiClient's response interceptor calls normalizeError() on ALL Axios errors before
			// this catch block — so err is always ApiError here, never a raw AxiosError.
			// Network errors (timeout, no response) also arrive as ApiError (status: null),
			// producing CartApiError with errors: [].
			if (err instanceof ApiError) {
				const axiosData = (err.raw as { response?: { data?: { errors?: unknown[] } } })
					?.response?.data
				const domainErrors: CalculationDomainError[] = Array.isArray(axiosData?.errors)
					? axiosData.errors.filter(isCalculationDomainError)
					: []
				throw new CartApiError(domainErrors, err.status, err.raw)
			}
			throw err
		})
}

// NOTE: validateDistance stub removed — P2-S4-02 implemented the real function in
// src/shared/api/delivery.ts (uses oocApi + oocUrls.validateDistance). Import from there.
